'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function CTASection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const reveals = sectionRef?.current?.querySelectorAll('.reveal');
    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className="relative rounded-4xl overflow-hidden p-12 md:p-16 lg:p-20 reveal"
          style={{
            background: 'linear-gradient(135deg, #13132A 0%, #0D0D1A 50%, #1A1A0D 100%)',
            border: '1px solid rgba(245,166,35,0.15)',
          }}
        >
          {/* Top right glow */}
          <div
            className="absolute top-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none animate-blob"
            style={{
              background: 'radial-gradient(circle, rgba(245,166,35,0.15) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          />
          {/* Bottom left glow */}
          <div
            className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full pointer-events-none animate-blob-reverse"
            style={{
              background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)',
              filter: 'blur(80px)',
            }}
          />
          <div className="absolute inset-0 noise-overlay opacity-30 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-accent/20 bg-accent/5 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent" />
              </span>
              <span className="text-xs font-700 text-accent tracking-widest uppercase">Ready to Grow?</span>
            </div>

            {/* Heading */}
            <h2
              className="font-display font-800 text-foreground tracking-tight mb-6"
              style={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 5vw, 3.75rem)',
                lineHeight: 1.1,
              }}
            >
              Your App &amp; Website Should Be<br />
              <span className="text-gradient-amber">Working for You.</span>
            </h2>

            {/* Description */}
            <p className="text-lg text-muted font-400 leading-relaxed mb-10 max-w-xl mx-auto">
              Get a free consultation on how we can build your app or web presence, drive buyers through Meta, TikTok &amp; Google Ads, and add affiliate revenue as a passive layer — all working together.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-accent text-primary text-base font-700 rounded-full hover:bg-accent-light transition-all duration-300 hover:-translate-y-1 accent-glow"
                style={{ color: '#0d0d1a', fontWeight: 700 }}
              >
                Get Free Consultation
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
              <Link
                href="/faq"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 glass-card text-foreground text-base font-600 rounded-full hover:border-accent/30 transition-all duration-300 hover:-translate-y-1"
              >
                Read FAQs
              </Link>
            </div>

            {/* Contact Info */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted">
              <a
                href="mailto:Getconnect@campyellow.in"
                className="flex items-center gap-2 hover:text-accent transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                Getconnect@campyellow.in
              </a>
              <a
                href="tel:+91919241197993"
                className="flex items-center gap-2 hover:text-accent transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.4 2 2 0 0 1 3 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                +91 919241197993
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
